import { useEffect, useRef } from 'react';

/**
 * UP-17 (2026-05-17): hook для закрытия popup'ов при клике/таче вне.
 *
 * @param isOpen — текущее состояние видимости (cb работает только когда true)
 * @param onOutsideClick — вызывается при mousedown / touchstart вне ref-элемента
 * @returns ref для оборачивающего div
 *
 * @example
 *   const ref = useOutsideClick<HTMLDivElement>(showMenu, () => setShowMenu(false));
 *   return <div ref={ref}>...</div>
 */
export function useOutsideClick<T extends HTMLElement>(
  isOpen: boolean,
  onOutsideClick: () => void,
) {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    function handle(event: MouseEvent | TouchEvent) {
      const target = event.target as Node | null;
      if (ref.current && target && !ref.current.contains(target)) {
        onOutsideClick();
      }
    }
    document.addEventListener('mousedown', handle);
    document.addEventListener('touchstart', handle);
    return () => {
      document.removeEventListener('mousedown', handle);
      document.removeEventListener('touchstart', handle);
    };
  }, [isOpen, onOutsideClick]);

  return ref;
}
